import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Loader2, ShieldCheck, RefreshCw, Check, X, Search, Plus, Trash2, Link2, BarChart3, Sparkles, ClipboardCheck, Building2 } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const STATUS_LABEL = { cleared: "Cleared", tos_pending: "ToS pending", requires_registration: "Requires registration", planned: "Planned" };
const STATUS_STYLE = { cleared: "bg-emerald-100 text-emerald-800", tos_pending: "bg-amber-100 text-amber-800", requires_registration: "bg-indigo-100 text-indigo-800", planned: "bg-zinc-100 text-zinc-700" };

export default function AdminSponsorReviews() {
  const [status, setStatus] = useState("pending");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reg, setReg] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [acting, setActing] = useState("");
  const [checkCo, setCheckCo] = useState("");
  const [checkRes, setCheckRes] = useState(null);
  const [aliases, setAliases] = useState([]);
  const [aliasBrand, setAliasBrand] = useState("");
  const [aliasResults, setAliasResults] = useState([]);
  const [aliasSearching, setAliasSearching] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const [supply, setSupply] = useState(null);
  const [supplyRunning, setSupplyRunning] = useState(false);
  const [supplyHistory, setSupplyHistory] = useState([]);
  const [compliance, setCompliance] = useState(null);
  const [edits, setEdits] = useState({});
  const [savingKey, setSavingKey] = useState("");
  const [boards, setBoards] = useState(null);
  const [boardForm, setBoardForm] = useState({ provider: "greenhouse", token: "", employer: "" });
  const [boardBusy, setBoardBusy] = useState(false);
  const [purchasable, setPurchasable] = useState(false);
  const [togglingGate, setTogglingGate] = useState(false);
  const [nhsIngest, setNhsIngest] = useState(null);
  const [health, setHealth] = useState(null);
  const [spRoles, setSpRoles] = useState(null);
  const [roleForm, setRoleForm] = useState({ category: "Medical", name: "" });

  const load = async (st = status) => {
    setLoading(true);
    try {
      const [rev, s, al] = await Promise.all([
        api.get(`/admin/sponsor-reviews?status=${st}`),
        api.get("/admin/sponsor-register/status"),
        api.get("/admin/sponsor-aliases"),
      ]);
      setData(rev.data);
      setReg(s.data);
      setAliases(al.data.items || []);
      api.get("/admin/sponsor-supply").then((r) => setSupply(r.data)).catch(() => {});
      api.get("/admin/sponsor-supply/history").then((r) => setSupplyHistory(r.data.items || [])).catch(() => {});
      api.get("/admin/source-compliance").then((r) => setCompliance(r.data)).catch(() => {});
      api.get("/admin/ats-boards").then((r) => setBoards(r.data)).catch(() => {});
      api.get("/admin/sponsored-settings").then((r) => setPurchasable(!!r.data.purchasable)).catch(() => {});
      api.get("/admin/nhs-ingest").then((r) => setNhsIngest(r.data)).catch(() => {});
      api.get("/admin/supply-health").then((r) => setHealth(r.data)).catch(() => {});
      api.get("/admin/sponsored-roles").then((r) => setSpRoles(r.data)).catch(() => {});
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    load(status);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  const sync = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/admin/sponsor-register/sync");
      toast.success(`Synced ${data.record_count?.toLocaleString()} sponsors`);
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Sync failed"); }
    finally { setSyncing(false); }
  };

  const resolve = async (id, decision) => {
    setActing(id + decision);
    try {
      await api.post(`/admin/sponsor-reviews/${id}/resolve`, { decision });
      toast.success(decision === "confirm" ? "Confirmed as sponsor" : "Rejected");
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setActing(""); }
  };

  const runCheck = async () => {
    if (!checkCo.trim()) return;
    try {
      const { data } = await api.get(`/admin/sponsor-register/check?company=${encodeURIComponent(checkCo)}`);
      setCheckRes(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const aliasSearch = async () => {
    if (aliasBrand.trim().length < 2) return;
    setAliasSearching(true);
    try {
      const { data } = await api.get(`/admin/sponsor-register/search?q=${encodeURIComponent(aliasBrand)}`);
      setAliasResults(data.results || []);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setAliasSearching(false); }
  };

  const addAlias = async (matched_name) => {
    try {
      await api.post("/admin/sponsor-aliases", { company: aliasBrand, matched_name });
      toast.success(`Alias added: ${aliasBrand} → ${matched_name}`);
      setAliasBrand(""); setAliasResults([]);
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const deleteAlias = async (company_norm) => {
    try { await api.delete(`/admin/sponsor-aliases/${encodeURIComponent(company_norm)}`); toast.success("Alias removed"); await load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const seedAliases = async () => {
    setSeeding(true);
    try {
      const { data } = await api.post("/admin/sponsor-aliases/seed");
      toast.success(`Seeded ${data.seeded?.length || 0} common aliases`);
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSeeding(false); }
  };

  const saveCompliance = async (key, status, note) => {
    setSavingKey(key);
    try {
      await api.post("/admin/source-compliance", { key, status, note });
      toast.success("Source status updated");
      setEdits((e) => { const n = { ...e }; delete n[key]; return n; });
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSavingKey(""); }
  };

  const loadBoards = () => api.get("/admin/ats-boards").then((r) => setBoards(r.data)).catch(() => {});

  const addBoard = async () => {
    if (!boardForm.token.trim() || !boardForm.employer.trim()) { toast.error("Enter a board token and employer name."); return; }
    setBoardBusy(true);
    try {
      const { data } = await api.post("/admin/ats-boards", boardForm);
      toast.success(`Added ${data.employer} (${data.job_count} jobs)${data.sponsor_confirmed ? " · sponsor confirmed ✓" : " · NOT a confirmed sponsor"}`);
      setBoardForm({ ...boardForm, token: "", employer: "" });
      await loadBoards();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBoardBusy(false); }
  };

  const removeBoard = async (provider, token) => {
    try { const { data } = await api.delete(`/admin/ats-boards/${provider}/${encodeURIComponent(token)}`); toast.success(`Board ${data.action}`); await loadBoards(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const loadAllow = () => api.get("/admin/sponsored-settings").then((r) => setPurchasable(!!r.data.purchasable)).catch(() => {});
  const toggleGate = async (val) => {
    setTogglingGate(true);
    try { const { data } = await api.post("/admin/sponsored-settings", { purchasable: val }); setPurchasable(!!data.purchasable); toast.success(`Sponsored tier ${data.purchasable ? "ON — now purchasable" : "OFF"}`); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setTogglingGate(false); }
  };
  const runNhsIngest = async () => {
    try { await api.post("/admin/nhs-ingest/run"); toast.success("NHS ingest started — refresh in ~1 min to see the count."); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const runHealth = async () => {
    try { await api.post("/admin/supply-health/run"); toast.success("Supply-health check started — refresh in ~1 min."); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const loadSpRoles = () => api.get("/admin/sponsored-roles").then((r) => setSpRoles(r.data)).catch(() => {});
  const addSpRole = async () => {
    if (!roleForm.name.trim()) { toast.error("Enter a role name."); return; }
    try { await api.post("/admin/sponsored-roles", roleForm); toast.success("Role added"); setRoleForm({ ...roleForm, name: "" }); await loadSpRoles(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const delSpRole = async (id) => {
    try { await api.delete(`/admin/sponsored-roles/${id}`); toast.success("Role removed"); await loadSpRoles(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const runSupply = async () => {
    setSupplyRunning(true);
    try {
      const { data } = await api.post("/admin/sponsor-supply/run");
      setSupply(data);
      api.get("/admin/sponsor-supply/history").then((r) => setSupplyHistory(r.data.items || [])).catch(() => {});
      toast.success(`Supply check complete: ${data.confirm_rate_overall}% confirmed overall`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Supply check failed"); }
    finally { setSupplyRunning(false); }
  };

  return (
    <div className="space-y-6" data-testid="admin-sponsor-reviews">
      <div>
        <h1 className="font-heading text-2xl font-bold tracking-tight flex items-center gap-2"><ShieldCheck className="h-6 w-6 text-emerald-600" /> Sponsor Register</h1>
        <p className="text-sm text-muted-foreground mt-1">UK Home Office licensed-sponsor register + ambiguous-match review queue.</p>
      </div>

      <Card className="p-5 border-zinc-200" data-testid="sponsor-register-status">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="text-sm space-y-0.5">
            <p><strong>{(reg?.record_count ?? 0).toLocaleString()}</strong> licensed sponsors loaded</p>
            <p className="text-muted-foreground text-xs">Last synced: {reg?.last_synced ? new Date(reg.last_synced).toLocaleString() : "never"} · source dated {reg?.source_updated || "—"}</p>
          </div>
          <Button onClick={sync} disabled={syncing} variant="outline" data-testid="sponsor-sync-btn">
            {syncing ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-1.5" />} Sync register now
          </Button>
        </div>
        <div className="mt-4 flex gap-2">
          <div className="relative flex-1"><Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" /><Input className="pl-8" placeholder="Check an employer name…" value={checkCo} onChange={(e) => setCheckCo(e.target.value)} onKeyDown={(e) => e.key === "Enter" && runCheck()} data-testid="sponsor-check-input" /></div>
          <Button onClick={runCheck} variant="outline" data-testid="sponsor-check-btn">Check</Button>
        </div>
        {checkRes && (
          <p className="mt-2 text-sm" data-testid="sponsor-check-result">
            {checkRes.is_sponsor
              ? <span className="text-emerald-700">✓ {checkRes.confidence} — matched <strong>{checkRes.matched_name}</strong> ({checkRes.rating || "—"})</span>
              : <span className="text-muted-foreground">✗ not a confirmed sponsor ({checkRes.confidence})</span>}
          </p>
        )}
      </Card>

      <Card className="p-5 border-zinc-200" data-testid="source-compliance-card">
        <h2 className="font-heading text-base font-bold mb-1 flex items-center gap-2"><ClipboardCheck className="h-4 w-4 text-[#1a3060]" /> Source compliance</h2>
        <p className="text-xs text-muted-foreground mb-3">Single source of truth for where each data/job source stands for the launch gate. Editable as decisions are made.</p>
        <div className="space-y-2">
          {(compliance?.sources || []).map((s) => {
            const e = edits[s.key] || {};
            const status = e.status ?? s.status;
            const note = e.note ?? (s.note || "");
            const dirty = e.status !== undefined || e.note !== undefined;
            return (
              <div key={s.key} className="border border-zinc-200 rounded-lg p-3" data-testid={`source-${s.key}`}>
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <p className="font-semibold text-sm">{s.label} <span className="text-[10px] text-muted-foreground font-normal">· {s.category}</span></p>
                  <div className="flex items-center gap-1.5">
                    <span className={`text-[10px] uppercase font-semibold px-2 py-0.5 rounded ${s.wired ? "bg-emerald-100 text-emerald-800" : "bg-zinc-100 text-zinc-500"}`} data-testid={`source-wired-${s.key}`}>{s.wired ? "Live" : "Not wired"}</span>
                    <span className={`text-[10px] uppercase font-semibold px-2 py-0.5 rounded ${STATUS_STYLE[status]}`} data-testid={`source-status-${s.key}`}>{STATUS_LABEL[status]}</span>
                  </div>
                </div>
                <div className="flex items-center gap-2 mt-2">
                  <Select value={status} onValueChange={(v) => setEdits({ ...edits, [s.key]: { status: v, note } })}>
                    <SelectTrigger className="w-48 h-8 text-xs" data-testid={`source-status-select-${s.key}`}><SelectValue /></SelectTrigger>
                    <SelectContent>{(compliance?.statuses || []).map((st) => <SelectItem key={st} value={st}>{STATUS_LABEL[st]}</SelectItem>)}</SelectContent>
                  </Select>
                  <Input value={note} onChange={(ev) => setEdits({ ...edits, [s.key]: { status, note: ev.target.value } })} className="flex-1 text-xs h-8" data-testid={`source-note-${s.key}`} />
                  <Button size="sm" variant="outline" onClick={() => saveCompliance(s.key, status, note)} disabled={!dirty || savingKey === s.key} data-testid={`source-save-${s.key}`}>{savingKey === s.key ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}</Button>
                </div>
              </div>
            );
          })}
        </div>
      </Card>

      <Card className="p-5 border-zinc-200" data-testid="ats-boards-card">
        <h2 className="font-heading text-base font-bold mb-1 flex items-center gap-2"><Building2 className="h-4 w-4 text-[#1a3060]" /> Employer ATS boards</h2>
        <p className="text-xs text-muted-foreground mb-3">Add or remove the per-employer Greenhouse / Lever boards the Sponsored waterfall fetches from — no code changes. New boards are live-probed on add and checked against the sponsor register. Defaults can be disabled but not deleted.</p>
        <div className="flex flex-wrap gap-2 items-end">
          <div>
            <label className="text-[11px] uppercase tracking-wide text-muted-foreground font-semibold block mb-1">Provider</label>
            <Select value={boardForm.provider} onValueChange={(v) => setBoardForm({ ...boardForm, provider: v })}>
              <SelectTrigger className="w-32 h-9 text-sm" data-testid="board-provider-select"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="greenhouse">Greenhouse</SelectItem><SelectItem value="lever">Lever</SelectItem></SelectContent>
            </Select>
          </div>
          <div className="flex-1 min-w-[120px]">
            <label className="text-[11px] uppercase tracking-wide text-muted-foreground font-semibold block mb-1">Board token</label>
            <Input placeholder="e.g. monzo" value={boardForm.token} onChange={(e) => setBoardForm({ ...boardForm, token: e.target.value })} className="h-9" data-testid="board-token-input" />
          </div>
          <div className="flex-1 min-w-[140px]">
            <label className="text-[11px] uppercase tracking-wide text-muted-foreground font-semibold block mb-1">Employer display name</label>
            <Input placeholder="e.g. Monzo" value={boardForm.employer} onChange={(e) => setBoardForm({ ...boardForm, employer: e.target.value })} onKeyDown={(e) => e.key === "Enter" && addBoard()} className="h-9" data-testid="board-employer-input" />
          </div>
          <Button onClick={addBoard} disabled={boardBusy} data-testid="board-add-btn">{boardBusy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Plus className="h-4 w-4 mr-1.5" />} Add board</Button>
        </div>
        {["greenhouse", "lever"].map((prov) => (
          <div key={prov} className="mt-4" data-testid={`board-group-${prov}`}>
            <p className="text-xs uppercase tracking-wider text-muted-foreground font-semibold mb-1.5 capitalize">{prov} ({(boards?.[prov] || []).length})</p>
            <div className="space-y-1">
              {(boards?.[prov] || []).map((b) => (
                <div key={b.token} className={`flex items-center justify-between gap-2 text-sm border rounded-md px-3 py-1.5 ${b.source === "disabled" ? "border-zinc-200 bg-zinc-50 opacity-60" : "border-zinc-200"}`} data-testid={`board-${prov}-${b.token}`}>
                  <div className="min-w-0 flex items-center gap-2 flex-wrap">
                    <strong className="truncate">{b.employer}</strong>
                    <span className="font-mono text-[11px] text-muted-foreground">{b.token}</span>
                    <span className={`text-[10px] uppercase font-semibold px-1.5 py-0.5 rounded ${b.source === "custom" ? "bg-indigo-100 text-indigo-800" : b.source === "disabled" ? "bg-zinc-200 text-zinc-600" : "bg-zinc-100 text-zinc-600"}`}>{b.source}</span>
                    <span className={`text-[10px] uppercase font-semibold px-1.5 py-0.5 rounded ${b.sponsor_confirmed ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}`} title={b.sponsor_matched || ""}>{b.sponsor_confirmed ? "sponsor ✓" : "not confirmed"}</span>
                  </div>
                  {b.source !== "disabled" && (
                    <button onClick={() => removeBoard(prov, b.token)} className="text-red-500 shrink-0" title={b.source === "default" ? "Disable this default board" : "Remove board"} data-testid={`board-del-${prov}-${b.token}`}><Trash2 className="h-3.5 w-3.5" /></button>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}
      </Card>

      <Card className="p-5 border-zinc-200" data-testid="sponsored-access-card">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="font-heading text-base font-bold mb-1 flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-[#1a3060]" /> Sponsored tier access</h2>
            <p className="text-xs text-muted-foreground">The single on/off gate. When ON, any signed-in user can purchase the Sponsored tier. When OFF, checkout is blocked and the plan is hidden.</p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <span className={`text-xs font-semibold ${purchasable ? "text-emerald-700" : "text-muted-foreground"}`}>{purchasable ? "ON" : "OFF"}</span>
            <Switch checked={purchasable} onCheckedChange={toggleGate} disabled={togglingGate} data-testid="sponsored-gate-switch" />
          </div>
        </div>
        <div className="mt-3 flex items-center gap-2 text-xs rounded-md border px-3 py-2" data-testid="supply-health-badge">
          <span className={`inline-block h-2.5 w-2.5 rounded-full ${health?.status === "green" ? "bg-emerald-500" : health?.status === "amber" ? "bg-amber-500" : health?.status === "red" ? "bg-red-500" : "bg-zinc-300"}`} />
          <span><strong>Supply health:</strong> {health?.pct_hit_target != null ? `${health.pct_hit_target}% of ${health.profiles} sampled profiles hit the daily target of ${health.target}` : "not run yet"}{health?.threshold ? ` (green ≥ ${health.threshold}%)` : ""}</span>
          <Button size="sm" variant="outline" className="ml-auto h-7" onClick={runHealth} data-testid="run-health-btn">{health ? "Re-run" : "Run"}</Button>
        </div>
      </Card>

      <Card className="p-5 border-zinc-200" data-testid="nhs-ingest-card">
        <div className="flex items-center justify-between gap-3">
          <div>
            <h2 className="font-heading text-base font-bold mb-1 flex items-center gap-2"><ClipboardCheck className="h-4 w-4 text-[#1a3060]" /> NHS ingest</h2>
            <p className="text-xs text-muted-foreground">NHS vacancies ingested from jobs.nhs.uk into the store — matching reads from here (fewer live requests). {nhsIngest?.count != null ? `${nhsIngest.count} in store` : ""}{nhsIngest?.last_ingest_at ? ` · last ${new Date(nhsIngest.last_ingest_at).toLocaleString()}` : " · not run yet"}</p>
          </div>
          <Button size="sm" variant="outline" onClick={runNhsIngest} data-testid="run-nhs-ingest-btn">Run ingest</Button>
        </div>
      </Card>

      <Card className="p-5 border-zinc-200" data-testid="sponsored-roles-card">
        <h2 className="font-heading text-base font-bold mb-1 flex items-center gap-2"><ClipboardCheck className="h-4 w-4 text-[#1a3060]" /> Sponsored tier role list</h2>
        <p className="text-xs text-muted-foreground mb-3">The fixed roles Sponsored users pick from (max {spRoles?.max_select || 10}, no free text). Editable — grows over time.</p>
        <div className="flex flex-wrap gap-2 items-end">
          <div>
            <label className="text-[11px] uppercase tracking-wide text-muted-foreground font-semibold block mb-1">Category</label>
            <Select value={roleForm.category} onValueChange={(v) => setRoleForm({ ...roleForm, category: v })}>
              <SelectTrigger className="w-40 h-9 text-sm" data-testid="role-category-select"><SelectValue /></SelectTrigger>
              <SelectContent>{(spRoles?.categories || ["Medical", "Non-Medical"]).map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="flex-1 min-w-[160px]">
            <label className="text-[11px] uppercase tracking-wide text-muted-foreground font-semibold block mb-1">New role name</label>
            <Input placeholder="e.g. Clinical Coder" value={roleForm.name} onChange={(e) => setRoleForm({ ...roleForm, name: e.target.value })} onKeyDown={(e) => e.key === "Enter" && addSpRole()} className="h-9" data-testid="role-name-input" />
          </div>
          <Button onClick={addSpRole} data-testid="role-add-btn"><Plus className="h-4 w-4 mr-1.5" /> Add role</Button>
        </div>
        {(spRoles?.categories || []).map((cat) => (
          <div key={cat} className="mt-4" data-testid={`role-group-${cat}`}>
            <p className="text-xs uppercase tracking-wider text-muted-foreground font-semibold mb-1.5">{cat} ({(spRoles?.roles?.[cat] || []).length})</p>
            <div className="flex flex-wrap gap-1.5">
              {(spRoles?.roles?.[cat] || []).map((r) => (
                <span key={r.id} className="inline-flex items-center gap-1.5 text-xs bg-zinc-100 rounded-full pl-3 pr-1.5 py-1" data-testid={`sprole-${r.id}`}>
                  {r.name}
                  <button onClick={() => delSpRole(r.id)} className="text-red-500" data-testid={`sprole-del-${r.id}`}><Trash2 className="h-3 w-3" /></button>
                </span>
              ))}
            </div>
          </div>
        ))}
      </Card>

      <Card className="p-5 border-zinc-200" data-testid="sponsor-supply-card">
        <div className="flex items-center justify-between gap-3 mb-1">
          <h2 className="font-heading text-base font-bold flex items-center gap-2"><BarChart3 className="h-4 w-4 text-[#1a3060]" /> Sponsor supply dashboard</h2>
          <Button onClick={runSupply} disabled={supplyRunning} variant="outline" size="sm" data-testid="supply-run-btn">
            {supplyRunning ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-1.5" />} Run supply check
          </Button>
        </div>
        <p className="text-xs text-muted-foreground mb-3">Live sample of aggregator listings — how many surfaced employers hold a confirmed sponsor licence vs recruitment-agency intermediaries. This is the launch-gate metric.</p>
        {!supply?.ran_at ? (
          <p className="text-sm text-muted-foreground" data-testid="supply-empty">No sample yet — run a supply check.</p>
        ) : (
          <div data-testid="supply-stats">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-3"><p className="text-2xl font-bold text-emerald-700">{supply.confirm_rate_overall}%</p><p className="text-[11px] text-emerald-800/80">confirmed overall</p></div>
              <div className="rounded-lg bg-zinc-50 border border-zinc-200 p-3"><p className="text-2xl font-bold">{supply.confirm_rate_direct}%</p><p className="text-[11px] text-muted-foreground">confirmed of direct employers</p></div>
              <div className="rounded-lg bg-amber-50 border border-amber-200 p-3"><p className="text-2xl font-bold text-amber-700">{supply.agency_pct}%</p><p className="text-[11px] text-amber-800/80">recruitment-agency names</p></div>
              <div className="rounded-lg bg-zinc-50 border border-zinc-200 p-3"><p className="text-2xl font-bold">{supply.distinct_companies}</p><p className="text-[11px] text-muted-foreground">distinct employers sampled</p></div>
            </div>
            <p className="text-xs text-muted-foreground mt-3">Of {supply.direct_named} direct employers: <strong className="text-emerald-700">{supply.direct_confirmed} confirmed</strong>, {supply.direct_review} to review, {supply.direct_none} none · {supply.sampled_postings} postings across {supply.queries?.length} role types · last run {new Date(supply.ran_at).toLocaleString()}</p>
            {supply.ats_distinct != null && (
              <p className="text-xs mt-2 rounded-md bg-indigo-50 border border-indigo-200 text-indigo-800 px-3 py-2" data-testid="supply-ats-line">
                <strong>ATS direct sourcing (Greenhouse + Lever):</strong> +{supply.ats_confirmed}/{supply.ats_distinct} register-confirmed employers folded in. Aggregator alone = <strong>{supply.confirm_rate_aggregator}%</strong> → blended overall = <strong className="text-emerald-700">{supply.confirm_rate_overall}%</strong>.
              </p>
            )}
            {supplyHistory.length > 1 && (
              <div className="mt-3" data-testid="supply-trend">
                <p className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold mb-1">Confirm-rate trend (overall)</p>
                <div className="flex items-end gap-1 h-16">
                  {supplyHistory.map((h, i) => (
                    <div key={i} className="flex-1 flex flex-col items-center justify-end group" title={`${new Date(h.ran_at).toLocaleString()} · ${h.confirm_rate_overall}% overall · ${h.agency_pct}% agency`}>
                      <span className="text-[9px] text-muted-foreground">{h.confirm_rate_overall}%</span>
                      <div className="w-full bg-emerald-500/70 group-hover:bg-emerald-600 rounded-t" style={{ height: `${Math.max(4, h.confirm_rate_overall)}%` }} />
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </Card>

      <Card className="p-5 border-zinc-200" data-testid="sponsor-aliases-card">
        <div className="flex items-center justify-between gap-2 mb-1">
          <h2 className="font-heading text-base font-bold flex items-center gap-2"><Link2 className="h-4 w-4 text-[#1a3060]" /> Confirmed employer aliases</h2>
          <Button onClick={seedAliases} disabled={seeding} variant="outline" size="sm" data-testid="alias-seed-btn">
            {seeding ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Sparkles className="h-4 w-4 mr-1.5" />} Seed common aliases
          </Button>
        </div>
        <p className="text-xs text-muted-foreground mb-3">Map a brand / short name to its exact register entity so matching confirms it (e.g. “Mastercard” → “Mastercard UK Management Services LTD”). Register-validated — no fabrication.</p>
        <div className="flex gap-2">
          <Input placeholder="Brand / employer name as it appears on job posts…" value={aliasBrand} onChange={(e) => setAliasBrand(e.target.value)} onKeyDown={(e) => e.key === "Enter" && aliasSearch()} data-testid="alias-brand-input" />
          <Button onClick={aliasSearch} disabled={aliasSearching} variant="outline" data-testid="alias-search-btn">{aliasSearching ? <Loader2 className="h-4 w-4 animate-spin" /> : "Find in register"}</Button>
        </div>
        {aliasResults.length > 0 && (
          <div className="mt-2 space-y-1" data-testid="alias-results">
            {aliasResults.map((r) => (
              <button key={r.name} onClick={() => addAlias(r.name)} className="w-full text-left text-sm border border-zinc-200 rounded-md px-3 py-2 hover:bg-emerald-50 flex items-center justify-between" data-testid="alias-result">
                <span>{r.name} <span className="text-muted-foreground text-xs">({r.rating || "—"})</span></span>
                <Plus className="h-4 w-4 text-emerald-600 shrink-0" />
              </button>
            ))}
          </div>
        )}
        {aliases.length > 0 && (
          <div className="mt-4 space-y-1" data-testid="alias-list">
            <p className="text-xs uppercase tracking-wider text-muted-foreground font-semibold mb-1">Existing ({aliases.length})</p>
            {aliases.map((a) => (
              <div key={a.company_norm} className="flex items-center justify-between text-sm border-b border-zinc-100 pb-1.5" data-testid={`alias-${a.company_norm}`}>
                <span className="truncate"><strong>{a.display_company || a.company_norm}</strong> → {a.matched_name}</span>
                <button onClick={() => deleteAlias(a.company_norm)} className="text-red-500 shrink-0 ml-2" data-testid={`alias-del-${a.company_norm}`}><Trash2 className="h-3.5 w-3.5" /></button>
              </div>
            ))}
          </div>
        )}
      </Card>

      <div className="flex gap-2" data-testid="sponsor-review-tabs">
        {["pending", "approved", "rejected"].map((s) => (
          <button key={s} onClick={() => setStatus(s)} data-testid={`sponsor-tab-${s}`}
            className={`px-3 py-1.5 rounded-lg text-sm capitalize ${status === s ? "bg-[#1a3060] text-white font-semibold" : "bg-white border border-zinc-200"}`}>
            {s} {data?.counts?.[s] != null ? `(${data.counts[s]})` : ""}
          </button>
        ))}
      </div>

      {loading ? <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div> : (
        <Card className="border-zinc-200 divide-y divide-zinc-100" data-testid="sponsor-review-list">
          {(data?.items || []).length === 0 && <p className="p-6 text-sm text-muted-foreground text-center">No {status} reviews.</p>}
          {(data?.items || []).map((r) => (
            <div key={r.id} className="p-4 flex items-center justify-between gap-4" data-testid={`sponsor-review-${r.id}`}>
              <div className="min-w-0 text-sm">
                <p className="truncate"><strong>{r.company}</strong></p>
                <p className="text-muted-foreground text-xs truncate">candidate match: {r.matched_name} · score {r.score}</p>
              </div>
              {status === "pending" && (
                <div className="flex gap-2 shrink-0">
                  <Button size="sm" variant="outline" className="text-emerald-700 border-emerald-300" onClick={() => resolve(r.id, "confirm")} disabled={acting === r.id + "confirm"} data-testid={`sponsor-confirm-${r.id}`}>
                    {acting === r.id + "confirm" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5 mr-1" />} Confirm
                  </Button>
                  <Button size="sm" variant="outline" className="text-red-600 border-red-300" onClick={() => resolve(r.id, "reject")} disabled={acting === r.id + "reject"} data-testid={`sponsor-reject-${r.id}`}>
                    {acting === r.id + "reject" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <X className="h-3.5 w-3.5 mr-1" />} Reject
                  </Button>
                </div>
              )}
              {status !== "pending" && <span className="text-xs text-muted-foreground capitalize shrink-0">{r.status}</span>}
            </div>
          ))}
        </Card>
      )}
    </div>
  );
}
